import { TabPropsV2 } from '@evrekadev/evreka-ui-components'
import { UilSetting } from '@iconscout/react-unicons'
import { Button, Flex, Tab, TabList, Tabs, Typography } from 'components'
import { AI } from 'containers/AI/AI'
import { BreadcrumbsSkeleton } from 'containers/Layout/Skeletons/BreadcrumbsSkeleton'
import { DetailHeaderSkeleton } from 'containers/Layout/Skeletons/DetailHeaderSkeleton'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { selectUi } from 'services/slices/ui'
import { BreadCrumbType, useBreadCrumb } from 'utils/useBreadcrumb'
import { ClientSelection } from './ClientSelection'
import { DetailActionArea } from './DetailActionArea'
import { HeaderWrapperStyled, ProgressBarStyled, ResponsiveHeaderFlex } from './Header.style'
// import { AIBar } from 'containers/AI/AIBar'

export type DetailHeaderProps = {
  objectName?: string
  breadcrumbs?: Array<BreadCrumbType>
  headerExtra?: React.ReactNode
  action?: JSX.Element
  onHistory?: () => void
  onDelete?: () => void
  onEdit?: () => void
  onInsights?: () => void
  onDuplicate?: () => void
  onViewTracking?: () => void
  note?: {
    onNote: () => void
    isAdded: boolean
  }
  onPrintQR?: () => void
  loading?: boolean
}

export const DetailHeader: React.FC<DetailHeaderProps> = ({
  breadcrumbs,
  headerExtra,
  objectName,
  loading,
  ...rest
}) => {
  const breadcrumbsElement = useBreadCrumb(breadcrumbs ?? [])
  const navigate = useNavigate()
  const { settingsUrl, showLoadingBar } = useSelector(selectUi)

  return (
    <HeaderWrapperStyled data-testid="detail-header">
      {showLoadingBar && <ProgressBarStyled />}
      <ResponsiveHeaderFlex between="zero" middle="zero">
        {loading ? (
          <BreadcrumbsSkeleton />
        ) : breadcrumbs ? (
          breadcrumbsElement
        ) : (
          <Flex middle="zero" gap={0.5} data-testid="detail-header-info">
            <Typography
              fontSize="text_xl"
              fontWeight="semibold"
              color="grey900"
              data-testid="detail-name"
            >
              {objectName}
            </Typography>
            {headerExtra}
          </Flex>
        )}
        <Flex gap={0.75} middle="zero">
          {settingsUrl && (
            <Flex gap={0.25} middle="zero">
              <Button
                variant="text"
                color="secondary"
                onlyIcon
                iconLeft={UilSetting}
                onClick={() => navigate(settingsUrl)}
              />
            </Flex>
          )}
          <AI />
          <ClientSelection />
        </Flex>
      </ResponsiveHeaderFlex>
      {!loading && breadcrumbs && (
        <ResponsiveHeaderFlex between="zero" middle="zero">
          {breadcrumbs &&
            (loading ? (
              <DetailHeaderSkeleton />
            ) : (
              <Flex middle="zero" gap={0.5} data-testid="detail-header-info">
                <Typography
                  fontSize="text_xl"
                  fontWeight="semibold"
                  color="grey900"
                  data-testid="detail-name"
                >
                  {objectName}
                </Typography>
                {headerExtra}
              </Flex>
            ))}
          <DetailActionArea {...rest} />
        </ResponsiveHeaderFlex>
      )}
    </HeaderWrapperStyled>
  )
}

export type ListHeaderProps = {
  tabs?: Array<string>
  onTabChange?: TabPropsV2['onSelect']
  selectedIndex?: number
  breadcrumbs?: Array<BreadCrumbType>
  noTitle?: boolean
  customTitle?: string
  extra?: React.ReactNode
}

export const ListHeader: React.FC<ListHeaderProps> = ({
  tabs,
  onTabChange,
  selectedIndex,
  breadcrumbs,
  noTitle,
  customTitle,
  extra,
}) => {
  const { pageTitle, settingsUrl, showLoadingBar } = useSelector(selectUi)
  const breadcrumbsElement = useBreadCrumb(breadcrumbs ?? [])
  const navigate = useNavigate()
  return (
    <HeaderWrapperStyled hasTabs={!!tabs} data-testid="detail-header">
      {showLoadingBar && <ProgressBarStyled />}
      <ResponsiveHeaderFlex between="zero" middle="zero">
        {breadcrumbs && breadcrumbsElement}
        {!breadcrumbs && !noTitle && (
          <Typography fontSize="header_xs" fontWeight="semibold" color="grey900">
            {customTitle || pageTitle}
          </Typography>
        )}
        <Flex gap={0.75} middle="zero">
          {settingsUrl && (
            <Flex gap={0.25} middle="zero">
              <Button
                variant="text"
                color="secondary"
                onlyIcon
                iconLeft={UilSetting}
                onClick={() => navigate(settingsUrl)}
              />
            </Flex>
          )}
          {/* <AIBar /> */}

          <AI />
          <ClientSelection />
        </Flex>
      </ResponsiveHeaderFlex>
      {breadcrumbs && !noTitle && (
        <Typography fontSize="header_xs" fontWeight="semibold" color="grey900">
          {pageTitle}
        </Typography>
      )}
      <Flex between="zero" gap={1} middle="zero">
        {tabs && (
          <Tabs
            onSelect={onTabChange}
            selectedIndex={selectedIndex}
            style={{
              flex: 1,
              overflow: 'auto',
            }}
          >
            <TabList>
              {tabs.map((tab, i) => (
                <Tab key={i}>{tab}</Tab>
              ))}
            </TabList>
          </Tabs>
        )}
        <div
          style={{
            flexShrink: 0,
          }}
        >
          {extra}
        </div>
      </Flex>
    </HeaderWrapperStyled>
  )
}

export const FormHeader: React.FC<{ breadcrumbs?: BreadCrumbType[]; loading?: boolean }> = ({
  breadcrumbs,
  loading,
}) => {
  const breadcrumbsElement = useBreadCrumb(breadcrumbs ?? [])
  const navigate = useNavigate()
  const { settingsUrl, showLoadingBar } = useSelector(selectUi)
  return (
    <HeaderWrapperStyled data-testid="detail-header">
      {showLoadingBar && <ProgressBarStyled />}
      <ResponsiveHeaderFlex between="zero" middle="zero">
        {loading ? <BreadcrumbsSkeleton /> : breadcrumbsElement}
        <Flex gap={0.75} middle="zero">
          {settingsUrl && (
            <Flex gap={0.25} middle="zero">
              <Button
                variant="text"
                color="secondary"
                onlyIcon
                iconLeft={UilSetting}
                onClick={() => navigate(settingsUrl)}
              />
            </Flex>
          )}
          <AI />
          <ClientSelection />
        </Flex>
      </ResponsiveHeaderFlex>
    </HeaderWrapperStyled>
  )
}
